import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';
import { CartProduct, CART_CONSTANTS } from '../shared/components/FuncationsCartAndWishlist/Dto/AddToCardtUtilityDto';
import { handleAddToCart, handleAddToCartClick, batchAddToCart } from '../shared/components/FuncationsCartAndWishlist/AddToCardtUtility';
import { useAuth } from '../pages/Auth/AuthContext';
import { cartApiService } from '../services/cart.api.service';

interface CartContextType {
  cartItems: CartProduct[];
  addToCart: (product: any, quantity?: number, variantSku?: string) => Promise<void>;
  removeFromCart: (productId: string, variantSku?: string, variantId?: string) => void;
  updateQuantity: (productId: string, quantity: number, variantSku?: string, variantId?: string) => void;
  clearCart: () => Promise<void>;
  getCartTotal: () => number;
  getCartItemCount: () => number;
  handleAddToCart: typeof handleAddToCart;
  handleAddToCartClick: typeof handleAddToCartClick;
  batchAddToCart: typeof batchAddToCart;
  fetchCartFromBackend: () => Promise<void>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const [cartItems, setCartItems] = useState<CartProduct[]>([]);
  const isFetchingRef = useRef(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const wasAuthenticatedRef = useRef(isAuthenticated);

  const fetchCartFromBackend = useCallback(async () => {
    if (typeof window !== 'undefined' && sessionStorage.getItem('orderPlaced')) {
      setCartItems([]);
      return;
    }
    const response = await cartApiService.getCart();
    if (response.success && response.data) {
      const dataArray = Array.isArray(response.data) ? response.data : [];
      
      const transformedItems: CartProduct[] = dataArray.map((item: any) => {
        const variantTitle = item.variantInfo?.name 
          || item.product?.variantTitle 
          || item.product?.selectedVariant?.name 
          || item.product?.title 
          || item.product?.name;
        
        let categoryId: string | undefined;
        if (item.product?.categories && Array.isArray(item.product.categories)) {
          const firstCategory = item.product.categories[0];
          categoryId = typeof firstCategory === 'string' ? firstCategory : firstCategory?._id || firstCategory?.id;
        }
        
        return {
          productId: item.productId || item.product?.id || item.product?._id,
          slug: item.product?.slug,
          name: item.product?.title || item.product?.name || 'Product',
          price: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
          priceWithGST: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
          quantity: item.quantity || 1,
          variantSku: item.variantSku,
          variantId: item.variantId,
          variantTitle,
          imageUrl: item.variantImage || item.product?.imageUrl || item.product?.images?.[0]?.url || '',
          description: item.product?.description || '',
          categoryId: categoryId
        };
      });
      
      setCartItems(transformedItems);
    } else {
      setCartItems([]);
    }
  }, []);

  const mergeCartOnLogin = useCallback(async () => {
    if (isFetchingRef.current) return;
    
    isFetchingRef.current = true;
    let localCart: CartProduct[] = [];
    try {
      // Get local cart from localStorage
      localCart = typeof window !== 'undefined' ? 
        JSON.parse(localStorage.getItem('cart') || '[]') : [];
      
      
      if (localCart.length === 0) {
        await fetchCartFromBackend();
        return;
      }

      const guestItems = localCart.map((item: CartProduct) => ({
        productId: item.productId,
        quantity: item.quantity,
        variantSku: item.variantSku || undefined
      }));


      const mergeResponse = await cartApiService.mergeCart(guestItems);
      
      if (mergeResponse.success && mergeResponse.data) {
        // Transform merged data directly from response
        const dataArray = Array.isArray(mergeResponse.data) ? mergeResponse.data : [];
        
        const transformedItems: CartProduct[] = dataArray.map((item: any) => {
          const variantTitle = item.variantInfo?.name 
            || item.product?.variantTitle 
            || item.product?.selectedVariant?.name 
            || item.product?.title 
            || item.product?.name;
          
          let categoryId: string | undefined;
          if (item.product?.categories && Array.isArray(item.product.categories)) {
            const firstCategory = item.product.categories[0];
            categoryId = typeof firstCategory === 'string' ? firstCategory : firstCategory?._id || firstCategory?.id;
          }
          
          return {
            productId: item.productId || item.product?.id || item.product?._id,
            slug: item.product?.slug,
            name: item.product?.title || item.product?.name || 'Product',
            price: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
            priceWithGST: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
            quantity: item.quantity || 1,
            variantSku: item.variantSku,
            variantId: item.variantId,
            variantTitle,
            imageUrl: item.variantImage || item.product?.imageUrl || item.product?.images?.[0]?.url || '',
            description: item.product?.description || '',
            categoryId: categoryId
          };
        });
        
        setCartItems(transformedItems);
        
        if (typeof window !== 'undefined') {
          localStorage.removeItem('cart');
        }
      } else {
        await fetchCartFromBackend();
      }
      
    } catch (error) {
      if (localCart.length > 0) {
        setCartItems(localCart);
      } else {
        await fetchCartFromBackend();
        if (typeof window !== 'undefined') {
          localStorage.removeItem('cart');
        }
      }
    } finally {
      isFetchingRef.current = false;
      if (typeof window !== 'undefined') {
        localStorage.removeItem('cart');
      }
    }
  }, [fetchCartFromBackend]);

  useEffect(() => {
    if (isLoading) return;
    
    if (!isAuthenticated) {
      if (wasAuthenticatedRef.current) {
        setCartItems([]);
        if (typeof window !== 'undefined') {
          localStorage.removeItem('cart');
        }
        setHasInitialized(false);
      }
    } else if (isAuthenticated && !hasInitialized) {
      setHasInitialized(true);
      mergeCartOnLogin();
    }
    wasAuthenticatedRef.current = isAuthenticated;
  }, [isAuthenticated, isLoading, mergeCartOnLogin]); 

  useEffect(() => {
    if (!isAuthenticated && !isLoading && typeof window !== 'undefined') {
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }
    }
  }, [isAuthenticated, isLoading]);

  const addToCart = async (product: any, quantity: number = 1, variantSku?: string) => {
    const productIdToCheck = product.id || product._id;
    // Cart item limit removed
    
    if (isAuthenticated) {
      try {
        const payload = {
          productId: productIdToCheck,
          quantity,
          ...(variantSku && { 
            variantSku: variantSku,
            variantId: variantSku 
          })
        };
        
        const response = await cartApiService.addToCart(payload);
        
        if (response.success) {
          if (response.data && Array.isArray(response.data)) {
            const transformedItems: CartProduct[] = response.data.map((item: any) => {
              const variantTitle = item.variantInfo?.name 
                || item.product?.variantTitle 
                || item.product?.selectedVariant?.name 
                || item.product?.title 
                || item.product?.name;
              
              let categoryId: string | undefined;
              if (item.product?.categories && Array.isArray(item.product.categories)) {
                const firstCategory = item.product.categories[0];
                categoryId = typeof firstCategory === 'string' ? firstCategory : firstCategory?._id || firstCategory?.id;
              }
              
              return {
                productId: item.productId || item.product?.id || item.product?._id,
                slug: item.product?.slug,
                name: item.product?.title || item.product?.name || 'Product',
                price: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
                priceWithGST: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
                quantity: item.quantity || 1,
                variantSku: item.variantSku,
                variantId: item.variantId,
                variantTitle,
                imageUrl: item.variantImage || item.product?.imageUrl || item.product?.images?.[0]?.url || '',
                description: item.product?.description || '',
                categoryId: categoryId
              };
            });
            setCartItems(transformedItems);
          }
        } else {
        }
      } catch (error) {
      }
    } else {
      setCartItems(prevItems => {
        const existingItemIndex = prevItems.findIndex(
          item => {
            const isSameProduct = item.productId === productIdToCheck;
            const isSameVariant = (variantSku === undefined && item.variantSku === undefined) || 
                                  (item.variantSku === variantSku);
            return isSameProduct && isSameVariant;
          }
        );

        if (existingItemIndex > -1) {
          const updatedItems = [...prevItems];
          updatedItems[existingItemIndex].quantity += quantity;
          if (typeof window !== 'undefined') {
            localStorage.setItem('cart', JSON.stringify(updatedItems));
          }
          return updatedItems;
        } else {
          const variantTitle = product.variantTitle 
            || product.variantInfo?.name 
            || product.selectedVariant?.name;
          const newItem: CartProduct = {
            productId: productIdToCheck,
            slug: product.slug,
            name: product.name || product.title,
            price: product.price || 0,
            quantity,
            variantSku,
            variantId: variantSku,
            variantTitle,
            imageUrl: product.imageUrl || product.variantImage || product.image || '',
            description: product.description || ''
          };
          const newItems = [...prevItems, newItem];
          if (typeof window !== 'undefined') {
            localStorage.setItem('cart', JSON.stringify(newItems));
          }
          return newItems;
        }
      });
    }
  };

  const removeFromCart = (productId: string, variantSku?: string, variantIdFromCaller?: string) => {
    if (isAuthenticated) {
      const currentItem = cartItems.find(item => item.productId === productId && item.variantSku === variantSku);
      const variantId = variantIdFromCaller || currentItem?.variantId;
      cartApiService.removeFromCart(productId, variantSku, variantId)
        .then(response => {
          if (response.success && response.data && Array.isArray(response.data)) {
            const transformedItems: CartProduct[] = response.data.map((item: any) => {
              const variantTitle = item.variantInfo?.name 
                || item.product?.variantTitle 
                || item.product?.selectedVariant?.name 
                || item.product?.title 
                || item.product?.name;
              
              let categoryId: string | undefined;
              if (item.product?.categories && Array.isArray(item.product.categories)) {
                const firstCategory = item.product.categories[0];
                categoryId = typeof firstCategory === 'string' ? firstCategory : firstCategory?._id || firstCategory?.id;
              }
              
              return {
                productId: item.productId || item.product?.id || item.product?._id,
                slug: item.product?.slug,
                name: item.product?.title || item.product?.name || 'Product',
                price: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
                priceWithGST: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
                quantity: item.quantity || 1,
                variantSku: item.variantSku,
                variantId: item.variantId,
                variantTitle,
                imageUrl: item.variantImage || item.product?.imageUrl || item.product?.images?.[0]?.url || '',
                description: item.product?.description || '',
                categoryId: categoryId
              };
            });
            setCartItems(transformedItems);
          }
        })
        .catch(error => {
          console.error('Error removing from cart:', error);
        });
    } else {
      setCartItems(prevItems => {
        const filteredItems = prevItems.filter(item => {
          const isSameProduct = item.productId === productId;
          // Keep items that are NOT the same product
          if (!isSameProduct) return true;

          // If same product, check variant matching
          // If variantSku is undefined (non-variant product), remove if item also has no variantSku
          // If variantSku is defined, remove only if it matches
          if (variantSku === undefined || variantSku === '') {
            // Remove items with no variant (keep items with variants)
            return item.variantSku !== undefined && item.variantSku !== '';
          } else {
            // Remove items with matching variant (keep items with different variants)
            return item.variantSku !== variantSku;
          }
        });
        
        if (typeof window !== 'undefined') {
          localStorage.setItem('cart', JSON.stringify(filteredItems));
        }
        
        return filteredItems;
      });
    }
  };

  const updateQuantity = (productId: string, quantity: number, variantSku?: string, variantId?: string) => {
    if (quantity <= 0) {
      removeFromCart(productId, variantSku, variantId);
      return;
    }

    if (quantity > CART_CONSTANTS.MAX_QUANTITY) {
      console.warn(`Quantity limited to ${CART_CONSTANTS.MAX_QUANTITY}`);
      quantity = CART_CONSTANTS.MAX_QUANTITY;
    }

    if (isAuthenticated) {
      // Try to send both variantSku and variantId to backend to ensure correct matching
      const currentItem = cartItems.find(i => i.productId === productId && i.variantSku === variantSku);
      const variantIdToSend = variantId || currentItem?.variantId;

      cartApiService.updateQuantity(productId, quantity, variantSku, variantIdToSend)
        .then(response => {
          if (response.success && response.data && Array.isArray(response.data)) {
            const transformedItems: CartProduct[] = response.data.map((item: any) => ({
              productId: item.productId || item.product?.id || item.product?._id,
              slug: item.product?.slug,
              name: item.product?.title || item.product?.name || 'Product',
              price: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
              priceWithGST: item.priceWithGST ?? item.price ?? item.product?.price ?? 0,
              quantity: item.quantity || 1,
              variantSku: item.variantSku,
              variantId: item.variantId,
              imageUrl: item.variantImage || item.product?.imageUrl || item.product?.images?.[0]?.url || '',
              description: item.product?.description || ''
            }));
            setCartItems(transformedItems);
          }
        })
        .catch(error => {
          console.error('Error updating cart quantity:', error);
        });
    } else {
      setCartItems(prevItems => {
        const newItems = prevItems.map(item => {
          const isSameProduct = item.productId === productId;
          const isSameVariant = (variantSku === undefined && item.variantSku === undefined) || 
                                (item.variantSku === variantSku);
          
          return (isSameProduct && isSameVariant) ? { ...item, quantity } : item;
        });
        if (typeof window !== 'undefined') {
          localStorage.setItem('cart', JSON.stringify(newItems));
        }
        return newItems;
      });
    }
  };

  const clearCart = async () => {
    try {
      // Clear state immediately for better UX
      setCartItems([]);
      
      // Always clear localStorage to prevent cart from reappearing
      if (typeof window !== 'undefined') {
        localStorage.removeItem('cart');
        localStorage.removeItem('cartItems');
      }
      
      // If authenticated, clear cart on backend
      if (isAuthenticated) {
        await cartApiService.clearCart();
      }
    } catch (error) {
      console.error('Error clearing cart:', error);
      // Keep local state cleared even if API fails
    }
  };

  const getCartTotal = () => {
    const total = cartItems.reduce(
      (total, item) => total + (item.price || 0) * item.quantity,
      0
    );
    return Math.round(total);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  return (
    <CartContext.Provider 
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartItemCount,
        handleAddToCart,
        handleAddToCartClick,
        batchAddToCart,
        fetchCartFromBackend
      }}
    >
      {children}
    </CartContext.Provider>
  );
  }

export const useCart = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export default CartContext;
